import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Logo } from "@/components/SiteChrome";
import { GoogleAuthButton } from "@/components/GoogleAuthButton";
import { PasswordInput } from "@/components/PasswordInput";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const AuthShell = ({ title, subtitle, children, footer }) => (
  <div className="min-h-screen bg-sand">
    <div className="mx-auto flex max-w-md flex-col px-5 py-10 sm:py-16">
      <Logo />
      <div className="mt-8 rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm sm:p-8">
        <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900">{title}</h1>
        {subtitle && <p className="mt-2 text-sm text-slate-600">{subtitle}</p>}
        <div className="mt-7">{children}</div>
      </div>
      {footer && <div className="mt-6 text-center text-sm text-slate-600">{footer}</div>}
    </div>
  </div>
);

export default function Login() {
  const [form, setForm] = useState({ identifier: "", password: "" });
  const [busy, setBusy] = useState(false);
  const { applySession } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/api/auth/login", form);
      applySession(data);
      toast.success(`Welcome back, ${data.user.full_name.split(" ")[0]}`);
      navigate(data.next || "/", { replace: true });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      title="Log in to CARENEST"
      subtitle="Use your email address or the phone number you signed up with."
      footer={
        <>
          New to CARENEST?{" "}
          <Link to="/register" className="font-semibold text-emerald-800" data-testid="login-to-register-link">
            Create an account
          </Link>
        </>
      }
    >
      <GoogleAuthButton label="Log in with Google" />
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label htmlFor="identifier">Email address or phone number</Label>
          <Input
            id="identifier"
            required
            placeholder="you@example.com or +2348030000000"
            value={form.identifier}
            onChange={(e) => setForm({ ...form, identifier: e.target.value })}
            data-testid="login-email-input"
            className="mt-1.5"
          />
        </div>
        <div>
          <Label htmlFor="password">Password</Label>
          <PasswordInput
            id="password"
            required
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            testId="login-password-input"
            className="mt-1.5"
          />
        </div>
        <Link to="/forgot-password" className="block text-xs font-medium text-emerald-800" data-testid="forgot-password-link">
          Forgot your password?
        </Link>
        <Button type="submit" disabled={busy} className="w-full rounded-full bg-emerald-900 py-6 hover:bg-emerald-800" data-testid="login-submit-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Log in"}
        </Button>
      </form>
    </AuthShell>
  );
}
